/*
 * Emoji inside a plain string, drawn in the emoji face.
 *
 * The interface face carries no colour glyphs, so a character that a message row paints as 🎉 came
 * out of a tooltip as a tofu box on a machine whose system font has no emoji either (X-A1 D12).
 * The message renderer solves this by wrapping the emoji it parsed in `.tb-emoji`; a label cannot,
 * because it arrives already composed — the locale interpolates the emoji into a sentence. So the
 * run is found here, and the same class carries the same family.
 */

import type { ReactNode } from "react"
import "./ui.css"

/**
 * One emoji run: a pictograph with its variation selectors, skin tones and zero-width joins, or a
 * keycap (`1️⃣` is a digit, U+FE0F and U+20E3). The group is what `split` keeps, so a label comes
 * back as alternating text and emoji.
 */
const EMOJI_RUN =
	/([#*0-9]️⃣|\p{Extended_Pictographic}(?:\p{Emoji_Modifier}|️)*(?:‍\p{Extended_Pictographic}(?:\p{Emoji_Modifier}|️)*)*)/gu

/**
 * The text with every emoji run wrapped; a string with none comes back untouched.
 *
 * Anything that is not a string draws nothing. The signature says `string` and the callers are
 * checked against it, so this guard is about the one thing a type cannot promise: a label that came
 * out of `t()` for a key the running build does not carry. That used to arrive here as `undefined`,
 * and `undefined.split` took every conversation route down to the error boundary (X-B4 B4-17). Two
 * halves of that defect and both are now closed: `t()` answers a word for a key it does not know,
 * and a label that is not a string at all costs nothing but itself.
 */
export function emojiText(text: string): ReactNode {
	if (typeof text !== "string") return null
	const parts = text.split(EMOJI_RUN)
	if (parts.length === 1) return text
	return parts.map((part, index) =>
		index % 2 === 1 ? (
			<span key={`${String(index)}:${part}`} className="tb-emoji">
				{part}
			</span>
		) : (
			part
		),
	)
}
